import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  create,
  deleteProject,
  fetchAll,
  fetchOne,
  fetchProjectsStats,
  fetchRequestsStats,
  update,
} from '.';
import type { CreateProjectDto, Project, UpdateProjectDto } from './types';

export const useGetProjects = () => {
  return useQuery({
    queryKey: ['projects', 'list'],
    queryFn: () => fetchAll(),
    staleTime: 60 * 1000,
  });
};

export const useGetProject = (id?: string) => {
  return useQuery({
    queryKey: ['projects', 'details', id],
    queryFn: () => fetchOne(id as string),
    enabled: Boolean(id),
  });
};

export const useGetProjectRequestsStats = (id: string) => {
  return useQuery({
    queryKey: ['projects', 'stats', id],
    queryFn: () => fetchRequestsStats(id),
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: UpdateProjectDto }) => {
      return update(projectId, data);
    },
    onSuccess: (_, { projectId, data }) => {
      queryClient.setQueryData<Project>(['projects', 'details', projectId], (oldData) => {
        if (oldData) {
          return { ...oldData, ...data };
        }
      });
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
      // queryClient.invalidateQueries({ queryKey: ['projects', 'details', projectId] });
    },
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectDto) => {
      return create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
    },
  });
};

export const useDeleteProject = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      return deleteProject(id);
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['projects', 'details', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });
    },
  });
};

export const useGetProjectsStats = (id: string) => {
  return useQuery({
    queryKey: ['projects', 'stats', id],
    queryFn: () => fetchProjectsStats(id),
  });
};
